"use client";

import ProductLink from "@/components/navigation/ProductLink";
import { PackageIcon } from "@phosphor-icons/react";
import { useLocale, useTranslations } from "next-intl";
import type { EmbeddedProduct } from "@/lib/api/blog.server";
import { translateCityName } from "@/config/locations";
import { PriceDisplay } from "@/components/PriceDisplay";
import { PartnerNameLink } from "@/components/product-card/PartnerNameLink";
import { OptimizedImage } from "@/components/ui/optimized-image";
import type { SupportedCurrency } from "@/lib/utils/currency";
import type { Locale } from "@/i18n/routing";

interface Props {
  /** Locale prop kept for legacy callers; component reads via useLocale internally. */
  locale?: string;
  product: EmbeddedProduct;
}

const buildLocation = (product: EmbeddedProduct, locale: string) => {
  if (product.branchLocations && product.branchLocations.length > 0) {
    return product.branchLocations.join(", ");
  }
  if (product.primaryCity) {
    return translateCityName(
      product.primaryCity,
      locale as Locale,
    );
  }
  return null;
};

export function ProductCardEmbed({ product }: Props) {
  const locale = useLocale();
  const tPricing = useTranslations("product.pricing");
  const tBooking = useTranslations("product.booking");
  const tShipping = useTranslations("search.shippingOnly");
  const location = buildLocation(product, locale);

  return (
    <div className="not-prose my-6 group relative block no-underline">
      <div className="flex gap-3 sm:gap-4 items-start border border-black/8 rounded-3xl overflow-hidden bg-white hover:shadow-md transition-shadow">
        <div className="relative shrink-0 m-2 w-32 sm:w-40 aspect-square bg-zinc-100 overflow-hidden rounded-2xl">
          {product.thumbnailUrl ? (
            <OptimizedImage
              // 원본 object URL 을 그대로 넘긴다. OptimizedImage 의 loader 가
              // Supabase render 엔드포인트로 재작성한다. 여기서 미리 변환해
              // 넘기면 render URL 이 Supabase 판별을 통과하지 못해 Vercel
              // 최적화기(/_next/image)가 변환본을 한 번 더 처리·과금한다.
              src={product.thumbnailUrl}
              alt={product.name}
              fill
              // 2x DPR 디바이스 대비를 위해 실제 표시 폭의 2배로 잡음.
              // Next.js Image가 srcSet 후보 중 적절한 해상도를 선택.
              sizes="(max-width: 640px) 256px, 320px"
              className="object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full bg-linear-to-br from-zinc-100 to-zinc-200 flex items-center justify-center">
              <PackageIcon className="size-8 text-zinc-400" />
            </div>
          )}
          {product.isDeliveryOnly && (
            <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 bg-white/70 backdrop-blur-md rounded-full shadow-sm">
              <PackageIcon className="size-3 text-gray-800" />
              <span className="text-[10px] font-medium text-gray-900">
                {tShipping("badge")}
              </span>
            </div>
          )}
        </div>

        <div className="flex-1 py-3 pr-3 sm:py-4 sm:pr-4 flex flex-col justify-between min-w-0 gap-2">
          <div className="space-y-0.5">
            {/* 파트너명 — 1순위 앵커. 블로그 임베드는 slug 가 없어 평문으로 렌더된다. */}
            <PartnerNameLink
              name={product.partnerName ?? undefined}
              className="text-[13px] leading-4.5 font-semibold text-gray-900"
            />
            {/* 상품명 — 2순위. after:inset-0 이 카드 전체를 덮는 상품 링크. */}
            <ProductLink
              href={`/${locale}/products/${product.id}`}
              className="block text-sm sm:text-base/5 font-medium text-gray-900 line-clamp-2 group-hover:text-orange-600 transition-colors after:absolute after:inset-0 after:content-['']"
            >
              {product.name}
            </ProductLink>
            {location && (
              <p className="text-[11px] leading-3.75 text-gray-500 truncate">{location}</p>
            )}
            {product.pricePerDay !== null && product.pricePerDay > 0 && (
              <p
                className="pt-0.5 text-base sm:text-lg font-bold text-gray-900"
                style={{ fontFamily: '"Pretendard Variable", sans-serif' }}
              >
                <PriceDisplay
                  price={product.pricePerDay}
                  sourceCurrency={
                    (product.currency as SupportedCurrency | undefined) ?? "USD"
                  }
                  size="md"
                  showCurrencyCode
                  className="text-base! sm:text-lg!"
                />
                <span className="ml-1 text-xs font-normal text-gray-500">
                  {tPricing("from")}
                </span>
              </p>
            )}
          </div>

          <div className="flex justify-end">
            <span className="inline-flex items-center justify-center min-w-28 sm:min-w-35 h-10 sm:h-11 px-5 rounded-full bg-orange-500 text-white text-sm sm:text-base font-semibold tracking-wide group-hover:bg-orange-600 transition-colors">
              {tBooking("reserve")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
